import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, AlertTriangle } from 'lucide-react';

interface ResourceSummary {
  id: string;
  name: string;
  type: 'human' | 'equipment' | 'facility' | 'software_license';
  availability: number;
  utilizationPercentage: number;
  skillLevel?: 'junior' | 'mid' | 'senior' | 'expert';
  hourlyRate: number;
  status: 'available' | 'busy' | 'offline';
}

interface TimeSlot {
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  utilizationPercentage: number;
  allocatedTasks: string[];
  reason?: string;
}

interface ResourceBooking {
  id: string;
  resourceId: string;
  taskId: string;
  projectId: string;
  startDateTime: string;
  endDateTime: string;
  status: 'tentative' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
}

interface ResourceConflict {
  id: string;
  type: 'overallocation' | 'calendar_conflict' | 'skill_mismatch' | 'availability_gap';
  severity: 'low' | 'medium' | 'high' | 'critical';
  resourceId: string;
  description: string;
}

interface ResourceCalendarProps {
  projectId?: string;
  viewMode: 'resource' | 'project' | 'team' | 'timeline';
  className?: string;
}

export const ResourceCalendar: React.FC<ResourceCalendarProps> = ({
  projectId,
  viewMode,
  className = ''
}) => {
  const [resources, setResources] = useState<ResourceSummary[]>([]);
  const [bookings, setBookings] = useState<ResourceBooking[]>([]);
  const [conflicts, setConflicts] = useState<ResourceConflict[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [timeScale, setTimeScale] = useState<'hour' | 'day' | 'week'>('day');
  const [filterType, setFilterType] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadResourceData();
  }, [projectId, selectedDate, viewMode]);

  const loadResourceData = async () => {
    setIsLoading(true);
    
    const mockResources: ResourceSummary[] = [
      {
        id: 'res-001',
        name: 'Sarah Chen',
        type: 'human',
        availability: 85,
        utilizationPercentage: 75,
        skillLevel: 'senior',
        hourlyRate: 95,
        status: 'available'
      },
      {
        id: 'res-002',
        name: 'Development Server',
        type: 'equipment',
        availability: 95,
        utilizationPercentage: 60,
        hourlyRate: 25,
        status: 'available'
      },
      {
        id: 'res-003',
        name: 'Conference Room A',
        type: 'facility',
        availability: 70,
        utilizationPercentage: 40,
        hourlyRate: 50,
        status: 'busy'
      }
    ];

    const mockBookings: ResourceBooking[] = [
      {
        id: 'book-001',
        resourceId: 'res-001',
        taskId: 'task-001',
        projectId: projectId || 'proj-001',
        startDateTime: `${selectedDate}T09:00:00Z`,
        endDateTime: `${selectedDate}T12:00:00Z`,
        status: 'confirmed',
        priority: 'high'
      }
    ];

    const mockConflicts: ResourceConflict[] = [
      {
        id: 'conf-001',
        type: 'overallocation',
        severity: 'medium',
        resourceId: 'res-001',
        description: 'Sarah Chen is overallocated by 2 hours on this date'
      }
    ];

    setResources(mockResources);
    setBookings(mockBookings);
    setConflicts(mockConflicts);
    setIsLoading(false);
  };

  const generateTimeSlots = (resourceId: string): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    const startHour = 8;
    const endHour = 18;

    for (let hour = startHour; hour < endHour; hour++) {
      const startTime = `${hour.toString().padStart(2, '0')}:00`;
      const endTime = `${(hour + 1).toString().padStart(2, '0')}:00`;
      
      const conflictingBookings = bookings.filter(booking => 
        booking.resourceId === resourceId &&
        booking.startDateTime.includes(selectedDate) &&
        parseInt(booking.startDateTime.split('T')[1].split(':')[0]) <= hour &&
        parseInt(booking.endDateTime.split('T')[1].split(':')[0]) > hour
      );

      slots.push({
        startTime,
        endTime,
        isAvailable: conflictingBookings.length === 0,
        utilizationPercentage: conflictingBookings.length > 0 ? 100 : 0,
        allocatedTasks: conflictingBookings.map(b => b.taskId),
        reason: conflictingBookings.length > 0 ? 'Booked' : undefined
      });
    }

    return slots;
  };

  const filteredResources = resources.filter(resource => {
    if (filterType === 'all') return true;
    return resource.type === filterType;
  });

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 mx-auto mb-4" style={{ 
            border: '2px solid var(--cb-border)', 
            borderTop: '2px solid var(--cb-primary)' 
          }}></div>
          <div style={{ color: 'var(--cb-text-secondary)' }}>Loading resource calendar...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5" style={{ color: 'var(--cb-primary)' }} />
            <h3 className="text-xl font-semibold" style={{ 
              background: 'var(--cb-gradient-primary)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Resource Calendar
            </h3>
          </div>
          
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 rounded-lg text-sm transition-all duration-200 focus:scale-[1.02]"
            style={{
              background: 'var(--cb-input-bg)',
              border: '2px solid var(--cb-border)',
              color: 'var(--cb-text-primary)'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--cb-primary)';
              e.target.style.boxShadow = '0 0 0 3px var(--cb-primary-bg)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--cb-border)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div className="flex items-center gap-3">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 rounded-lg text-sm transition-all duration-200 focus:scale-[1.02]"
            style={{
              background: 'var(--cb-input-bg)',
              border: '2px solid var(--cb-border)',
              color: 'var(--cb-text-primary)'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--cb-primary)';
              e.target.style.boxShadow = '0 0 0 3px var(--cb-primary-bg)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--cb-border)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <option value="all">All Resources</option>
            <option value="human">Human Resources</option>
            <option value="equipment">Equipment</option>
            <option value="facility">Facilities</option>
            <option value="software_license">Software Licenses</option>
          </select>

          <select
            value={timeScale}
            onChange={(e) => setTimeScale(e.target.value as 'hour' | 'day' | 'week')}
            className="px-3 py-2 rounded-lg text-sm transition-all duration-200 focus:scale-[1.02]"
            style={{
              background: 'var(--cb-input-bg)',
              border: '2px solid var(--cb-border)',
              color: 'var(--cb-text-primary)'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--cb-primary)';
              e.target.style.boxShadow = '0 0 0 3px var(--cb-primary-bg)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--cb-border)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <option value="hour">Hourly View</option>
            <option value="day">Daily View</option>
            <option value="week">Weekly View</option>
          </select>
        </div>
      </div>

      {/* Resource Calendar Grid */}
      <div 
        className="rounded-lg overflow-hidden"
        style={{
          background: 'var(--cb-surface)',
          border: '1px solid var(--cb-border)',
          boxShadow: 'var(--cb-shadow-sm)'
        }}
      >
        {/* Enhanced Timeline Header */}
        <div className="grid grid-cols-[250px_1fr]" style={{ borderBottom: '2px solid var(--cb-border)' }}>
          <div className="p-6" style={{ 
            background: 'var(--cb-surface-secondary)', 
            borderRight: '2px solid var(--cb-border)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)'
          }}>
            <div className="flex items-center gap-3">
              <User className="w-5 h-5" style={{ color: 'var(--cb-primary)' }} />
              <div>
                <div className="text-lg font-semibold" style={{ color: 'var(--cb-text-primary)' }}>Resources</div>
                <div className="text-xs" style={{ color: 'var(--cb-text-secondary)' }}>
                  {timeScale === 'hour' ? 'Hourly Timeline' : timeScale === 'day' ? 'Daily Timeline' : 'Weekly Timeline'}
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-px" style={{ background: 'var(--cb-border)' }}>
            {Array.from({ length: 12 }, (_, i) => {
              const timeLabel = timeScale === 'hour' ? `${8 + i}:00` : 
                               timeScale === 'day' ? `Day ${i + 1}` : 
                               `Week ${i + 1}`;
              return (
                <div key={i} className="p-3 text-center transition-all duration-200 hover:scale-105" style={{ 
                  background: 'var(--cb-surface-secondary)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)'
                }}>
                  <div className="text-sm font-semibold" style={{ color: 'var(--cb-text-primary)' }}>{timeLabel}</div>
                  {timeScale === 'hour' && (
                    <div className="text-xs mt-1" style={{ color: 'var(--cb-text-tertiary)' }}>
                      {new Date().toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Enhanced Resource Rows */}
        {filteredResources.map((resource, resourceIndex) => {
          const timeSlots = generateTimeSlots(resource.id);
          const utilizationColor = resource.utilizationPercentage > 80 ? 'var(--cb-danger)' :
                                 resource.utilizationPercentage > 60 ? 'var(--cb-warning)' :
                                 'var(--cb-success)';
          
          return (
            <div key={resource.id} className="grid grid-cols-[250px_1fr] last:border-b-0 transition-all duration-200 hover:bg-opacity-50" 
                 style={{ 
                   borderBottom: '1px solid var(--cb-border)',
                   background: resourceIndex % 2 === 0 ? 'transparent' : 'var(--cb-surface-hover)'
                 }}>
              {/* Enhanced Resource Info */}
              <div className="p-6" style={{ 
                background: 'var(--cb-surface-hover)', 
                borderRight: '2px solid var(--cb-border)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)'
              }}>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className={`w-4 h-4 rounded-full transition-all duration-200 ${
                      resource.status === 'available' ? 'bg-green-500 shadow-green-500/50' :
                      resource.status === 'busy' ? 'bg-yellow-500 shadow-yellow-500/50' :
                      'bg-red-500 shadow-red-500/50'
                    }`} style={{ boxShadow: '0 0 8px currentColor' }} />
                    <div className={`absolute inset-0 w-4 h-4 rounded-full animate-pulse ${
                      resource.status === 'available' ? 'bg-green-500' :
                      resource.status === 'busy' ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`} style={{ opacity: 0.3 }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="text-base font-semibold" style={{ color: 'var(--cb-text-primary)' }}>{resource.name}</div>
                      {resource.skillLevel && (
                        <div className="px-2 py-1 rounded-full text-xs font-medium" style={{
                          background: 'var(--cb-primary-bg)',
                          color: 'var(--cb-primary)',
                          border: '1px solid var(--cb-primary)'
                        }}>
                          {resource.skillLevel}
                        </div>
                      )}
                    </div>
                    <div className="text-sm capitalize mb-2" style={{ color: 'var(--cb-text-secondary)' }}>
                      {resource.type.replace('_', ' ')}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <div className="text-xs" style={{ color: 'var(--cb-text-tertiary)' }}>Availability:</div>
                        <div className="text-xs font-medium" style={{ color: 'var(--cb-success)' }}>{resource.availability}%</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="text-xs" style={{ color: 'var(--cb-text-tertiary)' }}>Utilization:</div>
                        <div className="text-xs font-medium" style={{ color: utilizationColor }}>{resource.utilizationPercentage}%</div>
                      </div>
                    </div>
                    <div className="mt-2 w-full bg-[#4267A1]/20 rounded-full h-2" style={{ background: 'var(--cb-surface-secondary)' }}>
                      <div 
                        className="h-2 rounded-full transition-all duration-300" 
                        style={{ 
                          width: `${resource.utilizationPercentage}%`,
                          background: `linear-gradient(90deg, ${utilizationColor}, ${utilizationColor}dd)`
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Time Slots */}
              <div className="grid grid-cols-12 gap-px" style={{ background: 'var(--cb-border)' }}>
                {Array.from({ length: 12 }, (_, index) => {
                  const slot = timeSlots[index] || { 
                    isAvailable: true, 
                    allocatedTasks: [], 
                    utilizationPercentage: 0,
                    startTime: '',
                    endTime: '',
                    reason: ''
                  };
                  
                  return (
                    <div key={index} className="min-h-[100px] p-2 relative group" style={{ 
                      background: 'var(--cb-surface)',
                      backdropFilter: 'blur(4px)',
                      WebkitBackdropFilter: 'blur(4px)'
                    }}>
                      {slot.allocatedTasks.length > 0 ? (
                        <div className="space-y-1">
                          {slot.allocatedTasks.map((taskId, taskIndex) => (
                            <div
                              key={taskIndex}
                              className="p-2 rounded-md text-xs font-medium truncate transition-all duration-200 hover:scale-105 cursor-pointer"
                              style={{
                                background: 'linear-gradient(135deg, var(--cb-primary-bg), var(--cb-primary))',
                                border: '1px solid var(--cb-primary)',
                                color: 'white',
                                boxShadow: 'var(--cb-shadow-sm)'
                              }}
                              title={`Task: ${taskId} (${slot.utilizationPercentage}% utilization)`}
                            >
                              {taskId}
                            </div>
                          ))}
                          <div className="text-xs text-center mt-1" style={{ color: 'var(--cb-text-tertiary)' }}>
                            {slot.utilizationPercentage}%
                          </div>
                        </div>
                      ) : (
                        <div className="h-full flex flex-col items-center justify-center transition-all duration-200 hover:scale-[1.02] rounded-md" style={{
                          background: slot.isAvailable ? 'var(--cb-surface-hover)' : 'var(--cb-danger-bg)',
                          border: `1px solid ${slot.isAvailable ? 'var(--cb-border)' : 'var(--cb-danger)'}`,
                          minHeight: '80px'
                        }}>
                          {slot.isAvailable ? (
                            <div className="text-center">
                              <div className="w-6 h-6 rounded-full mx-auto mb-1" style={{ 
                                background: 'var(--cb-success-bg)',
                                border: '2px solid var(--cb-success)'
                              }} />
                              <div className="text-xs font-medium" style={{ color: 'var(--cb-success)' }}>Available</div>
                            </div>
                          ) : (
                            <div className="text-center">
                              <AlertTriangle className="w-4 h-4 mx-auto mb-1" style={{ color: 'var(--cb-danger)' }} />
                              <div className="text-xs font-medium" style={{ color: 'var(--cb-danger)' }}>
                                {slot.reason || 'Unavailable'}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Hover overlay for additional info */}
                      <div className="absolute inset-0 bg-black bg-opacity-75 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center pointer-events-none">
                        <div className="text-white text-xs text-center p-2">
                          <div className="font-semibold">{resource.name}</div>
                          <div className="text-xs opacity-75">
                            {timeScale === 'hour' ? `${8 + index}:00 - ${9 + index}:00` : 
                             timeScale === 'day' ? `Day ${index + 1}` : 
                             `Week ${index + 1}`}
                          </div>
                          {slot.allocatedTasks.length > 0 && (
                            <div className="text-xs mt-1">
                              {slot.allocatedTasks.length} task{slot.allocatedTasks.length > 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Conflicts Summary */}
      {conflicts.length > 0 && (
        <div className="mt-6 p-4 rounded-lg" style={{
          background: 'var(--cb-danger-bg)',
          border: '1px solid var(--cb-danger)'
        }}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5" style={{ color: 'var(--cb-danger)' }} />
            <h4 className="text-lg font-semibold" style={{ color: 'var(--cb-danger)' }}>Resource Conflicts</h4>
          </div>
          <div className="space-y-2">
            {conflicts.map(conflict => (
              <div key={conflict.id} className="p-3 rounded transition-all duration-200 hover:scale-[1.01]" style={{
                background: 'var(--cb-surface-hover)',
                border: '1px solid var(--cb-danger)'
              }}>
                <div className="flex items-center justify-between">
                  <div className="text-sm" style={{ color: 'var(--cb-text-primary)' }}>{conflict.description}</div>
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    conflict.severity === 'critical' ? 'bg-red-600 text-white' :
                    conflict.severity === 'high' ? 'bg-orange-600 text-white' :
                    conflict.severity === 'medium' ? 'bg-yellow-600 text-black' :
                    'bg-green-600 text-white'
                  }`}>
                    {conflict.severity.toUpperCase()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enhanced Resource Utilization Summary */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="p-6 rounded-xl transition-all duration-300 hover:scale-[1.03] hover:rotate-1" style={{
          background: 'linear-gradient(135deg, var(--cb-surface-hover), var(--cb-surface))',
          border: '2px solid var(--cb-border)',
          boxShadow: 'var(--cb-shadow-lg)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl" style={{ 
              background: 'linear-gradient(135deg, var(--cb-info-bg), var(--cb-info))',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}>
              <User className="w-6 h-6" style={{ color: 'white' }} />
            </div>
            <div>
              <div className="text-3xl font-bold mb-1" style={{ 
                color: 'var(--cb-text-primary)',
                background: 'linear-gradient(135deg, var(--cb-primary), var(--cb-info))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>{resources.length}</div>
              <div className="text-sm font-medium" style={{ color: 'var(--cb-text-secondary)' }}>Total Resources</div>
              <div className="text-xs mt-1" style={{ color: 'var(--cb-text-tertiary)' }}>
                {filteredResources.length} filtered
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl transition-all duration-300 hover:scale-[1.03] hover:rotate-1" style={{
          background: 'linear-gradient(135deg, var(--cb-surface-hover), var(--cb-surface))',
          border: '2px solid var(--cb-border)',
          boxShadow: 'var(--cb-shadow-lg)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl" style={{ 
              background: 'linear-gradient(135deg, var(--cb-success-bg), var(--cb-success))',
              boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
            }}>
              <Clock className="w-6 h-6" style={{ color: 'white' }} />
            </div>
            <div>
              <div className="text-3xl font-bold mb-1" style={{ 
                color: 'var(--cb-text-primary)',
                background: 'linear-gradient(135deg, var(--cb-success), var(--cb-primary))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {Math.round(resources.reduce((sum, r) => sum + r.availability, 0) / resources.length)}%
              </div>
              <div className="text-sm font-medium" style={{ color: 'var(--cb-text-secondary)' }}>Avg Availability</div>
              <div className="text-xs mt-1" style={{ color: 'var(--cb-text-tertiary)' }}>
                {resources.filter(r => r.availability > 80).length} high availability
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl transition-all duration-300 hover:scale-[1.03] hover:rotate-1" style={{
          background: 'linear-gradient(135deg, var(--cb-surface-hover), var(--cb-surface))',
          border: '2px solid var(--cb-border)',
          boxShadow: 'var(--cb-shadow-lg)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl" style={{ 
              background: 'linear-gradient(135deg, var(--cb-primary-bg), var(--cb-primary))',
              boxShadow: 'var(--cb-shadow-purple)'
            }}>
              <Calendar className="w-6 h-6" style={{ color: 'white' }} />
            </div>
            <div>
              <div className="text-3xl font-bold mb-1" style={{ 
                color: 'var(--cb-text-primary)',
                background: 'linear-gradient(135deg, var(--cb-primary), var(--cb-warning))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>{bookings.length}</div>
              <div className="text-sm font-medium" style={{ color: 'var(--cb-text-secondary)' }}>Active Bookings</div>
              <div className="text-xs mt-1" style={{ color: 'var(--cb-text-tertiary)' }}>
                {bookings.filter(b => b.status === 'confirmed').length} confirmed
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl transition-all duration-300 hover:scale-[1.03] hover:rotate-1" style={{
          background: 'linear-gradient(135deg, var(--cb-surface-hover), var(--cb-surface))',
          border: '2px solid var(--cb-border)',
          boxShadow: 'var(--cb-shadow-lg)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl" style={{ 
              background: 'linear-gradient(135deg, var(--cb-warning-bg), var(--cb-warning))',
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
            }}>
              <AlertTriangle className="w-6 h-6" style={{ color: 'white' }} />
            </div>
            <div>
              <div className="text-3xl font-bold mb-1" style={{ 
                color: 'var(--cb-text-primary)',
                background: 'linear-gradient(135deg, var(--cb-warning), var(--cb-danger))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {Math.round(resources.reduce((sum, r) => sum + r.utilizationPercentage, 0) / resources.length)}%
              </div>
              <div className="text-sm font-medium" style={{ color: 'var(--cb-text-secondary)' }}>Avg Utilization</div>
              <div className="text-xs mt-1" style={{ color: 'var(--cb-text-tertiary)' }}>
                {resources.filter(r => r.utilizationPercentage > 80).length} overutilized
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
